import { toTagComparisonKey } from '../components/UserTagEditor.logic';
import dateformat from 'dateformat';

export type PlaceListView = 'inProgress' | 'completed';

export interface BulkDeleteTarget {
  id: number;
  name: string;
}

export type BulkDeleteOutcome = 'deleted' | 'failed';

export interface BulkDeleteResult {
  deletedCount: number;
  failedTargets: BulkDeleteTarget[];
  skippedTargets: BulkDeleteTarget[];
  cancelled: boolean;
}

export type BulkDeleteState =
  | { kind: 'idle' }
  | { kind: 'confirming'; targets: BulkDeleteTarget[] }
  | {
      kind: 'deleting';
      targets: BulkDeleteTarget[];
      finishedCount: number;
      cancelRequested: boolean;
    }
  | { kind: 'finished'; result: BulkDeleteResult };

// チェックボックスの切り替え。選ばれていた物件は選択から外し、選ばれていなかった物件は末尾に追加する
export function togglePlaceSelection(
  selectedPlaceIds: number[],
  placeId: number
): number[] {
  if (selectedPlaceIds.includes(placeId)) {
    return selectedPlaceIds.filter(
      (selectedPlaceId) => selectedPlaceId !== placeId
    );
  }
  return selectedPlaceIds.concat([placeId]);
}

// 表示中の物件がすべて選択されているか。表示中が 0 件なら false
export function areAllDisplayedSelected(
  selectedPlaceIds: number[],
  displayedPlaceIds: number[]
): boolean {
  if (displayedPlaceIds.length === 0) {
    return false;
  }
  return displayedPlaceIds.every((placeId) =>
    selectedPlaceIds.includes(placeId)
  );
}

// 見出しのチェックボックスの切り替え。表示中がすべて選択済みなら表示中だけを選択から外し、そうでなければ表示中をすべて選択に加える。
// 表示されていない選択は変えない
export function toggleAllDisplayedSelection(
  selectedPlaceIds: number[],
  displayedPlaceIds: number[]
): number[] {
  if (areAllDisplayedSelected(selectedPlaceIds, displayedPlaceIds)) {
    return selectedPlaceIds.filter(
      (placeId) => !displayedPlaceIds.includes(placeId)
    );
  }
  const notYetSelected = displayedPlaceIds.filter(
    (placeId) => !selectedPlaceIds.includes(placeId)
  );
  return selectedPlaceIds.concat(notYetSelected);
}

// 選択中の物件が 1 件以上あるときだけ下部バーを出す
export function isBulkBarVisible(selectedCount: number): boolean {
  return selectedCount > 0;
}

// 一覧を取得するときに、完了済の物件と進行中の物件のどちらを求めるかを決める
export function toCompletedQueryVariable(view: PlaceListView): boolean {
  if (view === 'completed') {
    return true;
  }
  return false;
}

// 一括更新で書き込む値。進行中の一覧では完了にし、完了済の一覧では進行中に戻す
export function toCompletedValueToWrite(view: PlaceListView): boolean {
  if (view === 'inProgress') {
    return true;
  }
  return false;
}

export function getBulkActionLabel(view: PlaceListView): string {
  if (view === 'completed') {
    return 'アーカイブ解除';
  }
  return 'アーカイブに移動';
}


// 表に出す物件が0件のときに、表の中に出す文。物件がそもそもないときと、絞り込みで0件になったときで分ける
export function getEmptyPlacesMessage(view: PlaceListView, totalCount: number): string {
  if (totalCount > 0) {
    return '条件に当てはまる物件はありません';
  }
  if (view === 'completed') {
    return 'アーカイブ済みの物件はありません';
  }
  return '物件はありません';
}

export function getBulkActionSuccessMessage(view: PlaceListView): string {
  if (view === 'completed') {
    return 'アーカイブを解除しました';
  }
  return 'アーカイブしました';
}

// 絞り込み欄で選んだタグを見分けるキー。同じ名前でも行（種類）が違えば別のタグとして扱う
export function toPlaceTagKey(tag: { kind: string; name: string }): string {
  return `${tag.kind}:${tag.name}`;
}

// 絞り込み欄でタグを並べる行。同じ名前でも、それぞれの行にすべて出す
export function groupTagsByRow<T extends { kind: string }>(tags: T[]): { userTags: T[]; chiefTags: T[]; customerTags: T[] } {
  return {
    userTags: tags.filter(tag => tag.kind === 'USER'),
    chiefTags: tags.filter(tag => tag.kind === 'CHIEF'),
    customerTags: tags.filter(tag => tag.kind === 'CUSTOMER')
  };
}

// 「条件に一致しない」にしたフィルタ。キーはフィルタの種類（USER / CHIEF / CUSTOMER / name / faces / created / updated）
export type NegatedFilters = { [kind: string]: boolean };

// 種類（タグ・責任者・お客様）ごとに、選んだもののどれかに当てはまる物件のID（同じ種類の中は OR）。何も選んでいない種類は入らない
export function findPlaceIdsByKind(tags: { kind: string; name: string; place_ids: number[] }[], selectedTagKeys: string[]): { [kind: string]: Set<number> } {
  const placeIdsByKind: { [kind: string]: Set<number> } = {};
  for (const tag of tags) {
    if (selectedTagKeys.indexOf(toPlaceTagKey(tag)) === -1) {
      continue;
    }
    const placeIds = placeIdsByKind[tag.kind] ?? new Set<number>();
    tag.place_ids.forEach(placeId => placeIds.add(placeId));
    placeIdsByKind[tag.kind] = placeIds;
  }
  return placeIdsByKind;
}

// タグ・責任者・お客様の条件にすべて当てはまるか（種類どうしは AND）。「一致しない」にした種類は、どれにも当てはまらないことを条件にする
export function matchesTagConditions(placeId: number, placeIdsByKind: { [kind: string]: Set<number> }, negated: NegatedFilters = {}): boolean {
  return Object.keys(placeIdsByKind).every(kind => placeIdsByKind[kind].has(placeId) !== !!negated[kind]);
}

// 「選択した条件以外の物件を表示」のときは、絞り込みに当てはまるかの判定を入れ替える。
// 条件が1つもないときは絞り込んでいないので、入れ替えずにすべての物件を表示する（standardの「選択したタグ以外の物件を表示」と同じ扱い）
export function applyFilterInversion(matched: boolean, inverted: boolean, hasConditions: boolean): boolean {
  if (inverted && hasConditions) {
    return !matched;
  }
  return matched;
}

// 絞り込みメニューの検索。表記ゆれ（全角半角・大文字小文字・空白）を無視して部分一致で探す
export function searchTagNames<T extends { name: string }>(tags: T[], query: string): T[] {
  const key = toTagComparisonKey(query);
  if (key === '') {
    return tags;
  }
  return tags.filter(tag => toTagComparisonKey(tag.name).indexOf(key) !== -1);
}

// タグ以外の絞り込み条件。入力欄の値をそのまま持ち、空文字は「指定なし」
export interface PlaceAttributeFilter {
  name: string;
  facesMin: string;
  facesMax: string;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
}

export const EMPTY_ATTRIBUTE_FILTER: PlaceAttributeFilter = {
  name: '',
  facesMin: '',
  facesMax: '',
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: ''
};

export type PlaceAttributeGroup = 'name' | 'faces' | 'created' | 'updated';

interface FilterablePlace {
  name: string;
  faces: unknown[];
  created: any;
  updated: any;
}

// 日付の入力欄（yyyy-mm-dd）と比べるため、日時を端末の日付に直す
export function toDateKey(value: any): string {
  return dateformat(value, 'yyyy-mm-dd');
}

function formatDateKey(dateKey: string): string {
  return dateKey.replace(/-/g, '/');
}

function toRangeText(from: string, to: string): string {
  return `${from}〜${to}`;
}

function parseCount(value: string): number | undefined {
  if (value.trim() === '') {
    return undefined;
  }
  const count = Number(value);
  return Number.isNaN(count) ? undefined : count;
}

// 選んだ条件をフィルタのボタンに出すための一覧。物件名の「を含む／を含まない」は、ボタンの側で条件の後ろに付ける
export function describeAttributeConditions(filter: PlaceAttributeFilter): { group: PlaceAttributeGroup; kind: string; text: string }[] {
  const conditions: { group: PlaceAttributeGroup; kind: string; text: string }[] = [];
  if (filter.name.trim() !== '') {
    conditions.push({ group: 'name', kind: '物件名', text: `「${filter.name.trim()}」` });
  }
  const facesMin = parseCount(filter.facesMin);
  const facesMax = parseCount(filter.facesMax);
  if (facesMin !== undefined || facesMax !== undefined) {
    conditions.push({ group: 'faces', kind: '面数', text: toRangeText(facesMin === undefined ? '' : `${facesMin}`, facesMax === undefined ? '' : `${facesMax}`) });
  }
  if (filter.createdFrom !== '' || filter.createdTo !== '') {
    conditions.push({ group: 'created', kind: '作成日', text: toRangeText(formatDateKey(filter.createdFrom), formatDateKey(filter.createdTo)) });
  }
  if (filter.updatedFrom !== '' || filter.updatedTo !== '') {
    conditions.push({ group: 'updated', kind: '更新日', text: toRangeText(formatDateKey(filter.updatedFrom), formatDateKey(filter.updatedTo)) });
  }
  return conditions;
}

// チップの × で外した種類の条件だけを空にする
export function clearAttributeGroup(filter: PlaceAttributeFilter, group: PlaceAttributeGroup): PlaceAttributeFilter {
  switch (group) {
    case 'name':
      return { ...filter, name: '' };
    case 'faces':
      return { ...filter, facesMin: '', facesMax: '' };
    case 'created':
      return { ...filter, createdFrom: '', createdTo: '' };
    case 'updated':
      return { ...filter, updatedFrom: '', updatedTo: '' };
  }
}

function isDateInRange(value: any, from: string, to: string): boolean {
  const dateKey = toDateKey(value);
  if (from !== '' && dateKey < from) {
    return false;
  }
  if (to !== '' && dateKey > to) {
    return false;
  }
  return true;
}

// タグ以外の条件にすべて当てはまるか。物件名は表記ゆれを無視した部分一致、面数と日付は両端を含む範囲。
// 「一致しない」にした種類は、当てはまらないことを条件にする（範囲なら範囲の外）
export function matchesAttributeFilter(place: FilterablePlace, filter: PlaceAttributeFilter, negated: NegatedFilters = {}): boolean {
  const nameKey = toTagComparisonKey(filter.name);
  const facesMin = parseCount(filter.facesMin);
  const facesMax = parseCount(filter.facesMax);
  // 条件を入れていない種類は undefined（絞り込まない）
  const groupMatches: [PlaceAttributeGroup, boolean | undefined][] = [
    ['name', nameKey === '' ? undefined : toTagComparisonKey(place.name).indexOf(nameKey) !== -1],
    [
      'faces',
      facesMin === undefined && facesMax === undefined
        ? undefined
        : (facesMin === undefined || place.faces.length >= facesMin) && (facesMax === undefined || place.faces.length <= facesMax)
    ],
    ['created', filter.createdFrom === '' && filter.createdTo === '' ? undefined : isDateInRange(place.created, filter.createdFrom, filter.createdTo)],
    ['updated', filter.updatedFrom === '' && filter.updatedTo === '' ? undefined : isDateInRange(place.updated, filter.updatedFrom, filter.updatedTo)]
  ];
  return groupMatches.every(([group, matched]) => matched === undefined || matched !== !!negated[group]);
}

export type PlaceSortKey = 'name' | 'faces' | 'chief_name' | 'client_name' | 'created' | 'updated';

export interface PlaceSort {
  key: PlaceSortKey;
  direction: 'asc' | 'desc';
}

// 並び替えメニューに出す項目。表の列と同じ順
export const PLACE_SORT_OPTIONS: { key: PlaceSortKey; label: string }[] = [
  { key: 'name', label: '物件名' },
  { key: 'faces', label: '面数' },
  { key: 'chief_name', label: '責任者' },
  { key: 'client_name', label: 'お客様' },
  { key: 'created', label: '作成日' },
  { key: 'updated', label: '更新日' }
];

export function getSortDirectionMark(direction: 'asc' | 'desc'): string {
  return direction === 'asc' ? '↑' : '↓';
}

// 並び替えボタンの文言。ボタンの前に「並び替え」の見出しを置くので、今の並び順（項目と向き）だけを出す
export function describePlaceSort(sort: PlaceSort | undefined): string {
  if (sort === undefined) {
    return '指定なし';
  }
  const option = PLACE_SORT_OPTIONS.find(o => o.key === sort.key);
  return `${option ? option.label : ''} ${getSortDirectionMark(sort.direction)}`;
}

// 並び替えメニューで項目を選んだときの並び順。向きは、メニューの先頭で選んでいる向き（項目を選ぶ前に選んだ向きを含む）にする
export function choosePlaceSortKey(selectedDirection: PlaceSort['direction'], key: PlaceSortKey): PlaceSort {
  return { key, direction: selectedDirection };
}

interface SortablePlace extends FilterablePlace {
  chief_name?: string | null;
  client_name?: string | null;
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, 'ja', { numeric: true });
}

// 並び替え。文字は五十音順（数字は数の大きさ順）、日付は日時順。空欄は昇順・降順どちらでも末尾にする。
// 同じ値どうしは元の順番を保つ
export function sortPlaces<T extends SortablePlace>(places: T[], sort: PlaceSort | undefined): T[] {
  if (sort === undefined) {
    return places;
  }
  const sign = sort.direction === 'asc' ? 1 : -1;
  const indexed = places.map((place, index) => ({ place, index }));
  indexed.sort((a, b) => {
    let result = 0;
    switch (sort.key) {
      case 'faces':
        result = (a.place.faces.length - b.place.faces.length) * sign;
        break;
      case 'created':
      case 'updated':
        result = (new Date(a.place[sort.key]).getTime() - new Date(b.place[sort.key]).getTime()) * sign;
        break;
      default: {
        const textA = a.place[sort.key] ?? '';
        const textB = b.place[sort.key] ?? '';
        if (textA === '' || textB === '') {
          result = (textA === '' ? 1 : 0) - (textB === '' ? 1 : 0);
        } else {
          result = compareText(textA, textB) * sign;
        }
      }
    }
    return result !== 0 ? result : a.index - b.index;
  });
  return indexed.map(entry => entry.place);
}

// 削除できた物件、失敗した物件、まだ実行していない物件を区別する
export function buildBulkDeleteResult(
  targets: BulkDeleteTarget[],
  outcomes: BulkDeleteOutcome[],
  cancelled: boolean
): BulkDeleteResult {
  return {
    deletedCount: outcomes.filter((outcome) => outcome === 'deleted').length,
    failedTargets: targets.filter(
      (_target, index) => outcomes[index] === 'failed'
    ),
    skippedTargets: targets.slice(outcomes.length),
    cancelled,
  };
}
